import { CurrencyEnum } from '@systemeio/types';
import { BadRequest } from '@systemeio/util';
import { defaultDigitalProductErrors } from 'modules/digital-product/constants/default-values';
import { DigitalProductSourceEnum } from 'modules/digital-product/enums/digital-product-source-enum';
import { useDigitalProduct } from 'modules/digital-product/hooks/use-digital-product';
import { useDigitalProductActions } from 'modules/digital-product/hooks/use-digital-product-actions';
import { useDigitalProductsList } from 'modules/digital-product/hooks/use-digital-product-list';
import { DigitalProductChangeInterface, DigitalProductErrorInterface, DigitalProductId, DigitalProductInterface } from 'modules/digital-product/types/digital-product-interface';
import { getDigitalProductApiData } from 'modules/digital-product/utils/get-digital-product-api-data';
import { getDigitalProductSwrKey } from 'modules/digital-product/utils/get-digital-product-swr-key';
import { DigitalOfferEditInterface } from 'modules/funnels/funnel/components/step-configuration-tab/offer-form-configuration-tab/types/digital-offer-interface';
import { useState } from 'react';
import { toast } from 'shared/components';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useSWRConfig } from 'swr';
interface UseDigitalProductSelectorProps {
  /** Product the offer currently links to, `null` when nothing is attached yet. */
  attachedId: DigitalProductId | null;
  currency: CurrencyEnum;
  /** Product just added in the modal: an existing one, or a new one without an id. */
  draftProduct?: DigitalProductChangeInterface;
  onAttach: (data: DigitalOfferEditInterface['digitalProduct']) => Promise<void>;
  /** Nothing is left to show: neither an attached product nor a draft. */
  onCleared: () => void;
  source: DigitalProductSourceEnum;
}

/**
 * Digital product of an offer. Saving an edited draft writes the product before attaching it.
 */
export function useDigitalProductSelector({
  attachedId,
  currency,
  draftProduct,
  onAttach,
  onCleared,
  source
}: UseDigitalProductSelectorProps) {
  const {
    t
  } = useLocoTranslation();
  const {
    mutate
  } = useSWRConfig();
  const {
    onCreate,
    onEdit
  } = useDigitalProductActions();
  const {
    mutate: mutateDigitalProductList
  } = useDigitalProductsList(currency, source);
  // The offer only links to a product, the product itself comes from its own endpoint.
  const {
    data: attachedProduct
  } = useDigitalProduct(attachedId);

  /**
   * Local working copy: what the modal added, or the attached product being edited. A second draft
   * cannot arrive while the card is mounted — the type selector is disabled once a product is
   * chosen — so the prop is only read as initial state.
   */
  const [draft, setDraft] = useState(draftProduct);
  // A product created from scratch has no id yet, so it opens straight in edit mode.
  const [isEditing, setIsEditing] = useState(!!draftProduct && !draftProduct.id);
  const [isFetching, setIsFetching] = useState(false);
  const [errors, setErrors] = useState<DigitalProductErrorInterface>(defaultDigitalProductErrors);

  // Outside of an edit the fetched product is the fresher one, the draft only covers the gap between
  // attaching a product and loading it back.
  const product = isEditing ? draft ?? attachedProduct : attachedProduct ?? draft;
  const isAttached = !!product?.id && product.id === attachedId;
  const isLoading = !product;
  const isReadyToSave = !!product?.name && product.pricePlans.length > 0;
  // An edit starts from the attached product itself and every change builds a new object. A
  // revalidation mid-edit also brings a new reference, which only allows a save that changes nothing.
  const isDirty = !isAttached || draft !== attachedProduct;
  const syncCaches = async (savedProduct: DigitalProductInterface) => {
    await mutate(getDigitalProductSwrKey(savedProduct.id), savedProduct, false);
    await mutateDigitalProductList(data => data?.some(el_0 => el_0.id === savedProduct.id) ? data.map(el => el.id === savedProduct.id ? savedProduct : el) : [...(data ?? []), savedProduct], false);
  };
  const save = async () => {
    if (!product || !isReadyToSave) return;
    setErrors(defaultDigitalProductErrors);
    setIsFetching(true);
    try {
      const apiData = getDigitalProductApiData(product, source);
      if ((isAttached || isEditing) && product.id) {
        const savedProduct_0 = await onEdit(product.id, apiData);
        await syncCaches(savedProduct_0);
        if (isAttached) {
          setDraft(undefined);
          // Only the product was written; attaching saves the offer, which reports success itself.
          toast.success(t('global.changes_saved'));
        } else {
          setDraft(savedProduct_0);
          await onAttach(savedProduct_0.id);
        }
        setIsEditing(false);
        return;
      }

      // An unchanged existing product only needs to be linked.
      if (product.id) {
        await onAttach(product.id);
        setIsEditing(false);
        return;
      }
      const createdProduct = await onCreate(apiData);
      await syncCaches(createdProduct);
      // Keeping it as the draft bridges the wait for its own request after the offer is saved.
      setDraft(createdProduct);
      await onAttach(createdProduct.id);
      setIsEditing(false);
    } catch (e) {
      if (e instanceof BadRequest && e.errors.fields) {
        setErrors(prev => ({
          ...prev,
          ...(e.errors.fields as DigitalProductErrorInterface)
        }));
      }
    } finally {
      setIsFetching(false);
    }
  };
  const changeProduct = (data_0: DigitalProductChangeInterface) => setDraft(data_0);
  const startEditing = () => {
    setDraft(product);
    setIsEditing(true);
  };

  /**
   * Cancelling an edit drops the local draft: the card falls back to the product saved on the offer,
   * or disappears entirely when nothing was attached yet.
   */
  const cancelEditing = () => {
    setDraft(undefined);
    setIsEditing(false);
    if (!attachedId) onCleared();
  };
  const removeProduct = async () => {
    if (attachedId) await onAttach(null);
    setDraft(undefined);
    onCleared();
  };
  return {
    cancelEditing,
    changeProduct,
    errors,
    isAttached,
    isDirty,
    isEditing,
    isFetching,
    isLoading,
    isReadyToSave,
    product,
    removeProduct,
    save,
    startEditing
  };
}